import * as React from 'react';
import { Modal } from './Modal';
import { Button } from './Button';

interface ActionModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  message: React.ReactNode;
  primaryLabel: string;
  onPrimary: () => void | Promise<void>;
  secondaryLabel?: string;
  onSecondary?: () => void;
  className?: string;
}

/**
 * Centered confirmation modal — title, message, primary action button, optional
 * secondary text link. Wraps the primary action in an `isProcessing` state so
 * async handlers disable the button while in flight.
 */
export const ActionModal: React.FC<ActionModalProps> = ({
  isOpen,
  onClose,
  title,
  message,
  primaryLabel,
  onPrimary,
  secondaryLabel,
  onSecondary,
  className,
}) => {
  const [isProcessing, setIsProcessing] = React.useState(false);

  const handlePrimary = async () => {
    setIsProcessing(true);
    try {
      await onPrimary();
    } catch {
      // The action owns its user-facing error state. Keep the modal open so the
      // user can retry or dismiss it without creating an unhandled rejection.
      return;
    } finally {
      setIsProcessing(false);
    }
  };

  const handleSecondary = () => {
    if (onSecondary) onSecondary();
    else onClose();
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} className={className ?? 'max-w-[388px]'}>
      <div className='flex flex-col items-center text-center'>
        <h2 className='mb-3 font-sans text-24 font-bold leading-tight text-dark-300'>{title}</h2>
        <p className='mb-6 font-sans text-16 leading-27 text-dark-200'>{message}</p>
        <Button
          onClick={handlePrimary}
          variant='create-echo'
          className='w-full'
          disabled={isProcessing}
          style={{
            fontFamily: 'Inter',
            fontWeight: 800,
            fontSize: '18px',
            lineHeight: '27px',
            color: '#1A1036',
          }}
        >
          {isProcessing ? 'Processing...' : primaryLabel}
        </Button>
        {secondaryLabel && (
          <button
            onClick={handleSecondary}
            className='mt-4 font-sans text-14 font-medium text-dark-200 transition-colors hover:text-dark-300'
          >
            {secondaryLabel}
          </button>
        )}
      </div>
    </Modal>
  );
};
